

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

// @ts-ignore
import draggable from "vuedraggable"

import DataTable from "../DataTable.vue"
import FieldSelector from "../Fields/FieldSelector.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import DefinitionTitle from "./DefinitionTitle.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

import UserImage from "../../UserImage.vue"

import User from "@/types/user"
import { DataDefinition, Field, DataDefinitionWithFields, CaseField, FieldType } from "@/types"

import api from "@/services/api"
import { FieldInstance, CaseAction } from "../../../types"
import AddSection from "../../Config/AddSection.vue"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"field-select": FieldSelector,
		"field-permissions": FieldPermissions,
		"user-image": UserImage,
		"definition-title": DefinitionTitle,
		draggable,
		"skeleton": SkeletonLoader,
		"add-section": AddSection
	}
})
export default class DefinitionSetup extends Vue {
	@Getter("getMe")
	public me!: User;

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Getter("getFieldTypes")
	fieldTypes!: FieldType[];

	@setup.Getter("getDataSourcesById")
	dataSourceDictionary!: { [key: string]: any };

	@setup.Action loadFields!: () => Promise<any[]>;
	@setup.Action loadDefinitions!: () => Promise<any[]>;

	@setup.Getter("getDefinitions")
	public definitions!: DataDefinition[];

	@setup.State
	fields!: Field[];

	disallowedFieldTypes = [
		"calculated",
		"time-spent",
		"user-role",
		"case-number",
		"file-upload",
		"file-only"
	];

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				filterable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "description",
				label: "Description",
				sortable: true,
				filterable: true
			},
			{
				key: "inheritedFromTitle",
				label: "Inherited From",
				dataType: "list",
				valueColumn: "inheritedFrom",
				options: this.inheritabledefinitions,
				sortable: true,
				inTable: false,
				description: "Include all fields from another definition. Changes to that definition will affect this one too.",
				dataCy: "inherited-from"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "actions",
				inModal: (item: any) => false,
				inTable: false,
				errors: (item: any) => item.actions && item.actions.some((action: CaseAction) => !action.title) ? "No title entered for action" : null
			}
		]
	}

	public get fieldColumns (): any[] {
		const columns = [
			{
				key: "handle",
				label: ""
			},
			{
				key: "field.title",
				label: "Title"
			},
			{
				key: "field.dataTypeTitle",
				label: "Data Type"
			},
			{
				key: "field.dataSourceTitle",
				label: "Data Source"
			},
			{
				key: "field.allowFiles",
				label: "Allow Files"
			},
			{
				key: "requiredRecord",
				label: "Required"
			},
			{
				key: "includeInTable",
				label: "Display in Table"
			}
		]

		if (this.definitionDetails && this.definitionDetails.specialType === "user") {
			columns.push(
				{
					key: "showInHeader",
					label: "Show in Header"
				})
			columns.push(
				{
					key: "allowSelfEdit",
					label: "Allow users to edit own"
				})
		}

		columns.push(
			{
				key: "remove",
				label: ""
			})
		return columns
	}

	public get definitionAllowedFieldTypes (): string[] {
		return this.fieldTypes.filter(f => !this.disallowedFieldTypes.includes(f.id)).map(f => f.id)
	}

	public get definitionAllowedFields (): Field[] {
		return this.fields
			? this.fields.filter(f => f.dataType && !this.disallowedFieldTypes.includes(f.dataType))
			: []
	}

	public definitionDetails: DataDefinitionWithFields | null = null;

	loading: boolean = true;
	multiSelectFields: boolean = false;

	public inheritedFromLoaded: string | null = null;
	public fieldFilter: string = "";

	public addFieldToSection: string | null = null;

	public get inheritFrom () {
		return this.definitionDetails ? this.definitionDetails.inheritedFrom : null
	}

	public get inheritabledefinitions (): DataDefinition[] {
		if (!this.definitionDetails || !this.definitionDetails.id) {
			return this.definitions
		}

		const inheritanceChain = new Set<string>()
		inheritanceChain.add(this.definitionDetails.id)
		const addInherited = (parent: DataDefinition) => {
			this.definitions.filter(child => child.inheritedFrom === parent.id).forEach(child => {
				if (child.id) {
					inheritanceChain.add(child.id)
					addInherited(child)
				}
			})
		}

		addInherited(this.definitionDetails)

		return this.definitions.filter(template => template.id && !inheritanceChain.has(template.id))
	}

	@Watch("inheritFrom")
	public async updateInherited (value: string, oldValue: string) {
		if (this.definitionDetails && value !== this.inheritedFromLoaded) {
			if (value) {
				const inheritedDefinition = await api.definitionDetails(value)

				this.definitionDetails.fields.removeWhere((field: FieldInstance) =>
					field.inheritedFrom != null && !inheritedDefinition.fields.some(
						(inheritedField: FieldInstance) => inheritedField.field.id === field.field.id))

				inheritedDefinition.fields.forEach((field: FieldInstance) => {
					if (this.definitionDetails && !this.definitionDetails.fields.some((existingField: FieldInstance) => existingField.field.id === field.field.id)) {
						if (!field.inheritedFrom) {
							field.inheritedFrom = inheritedDefinition.id!
							field.inheritedFromTitle = inheritedDefinition.title!
						}

						this.definitionDetails.fields.push(field)
					}
				})

				if (!this.definitionDetails.itemTitleFormat) {
					Vue.set(this.definitionDetails, "itemTitleFormat", inheritedDefinition.itemTitleFormat)
				}
			} else {
				Vue.set(this.definitionDetails, "inherited", [])
			}

			this.setDefinitionDetails(this.definitionDetails)
		}
	}

	public async mounted () {
		await this.loadDefinitions()
		this.loading = false

		await this.loadFields()
	}

	async loadDefinitionDetails (item: DataDefinition): Promise<DataDefinitionWithFields> {
		this.addFieldToSection = null
		return this.setDefinitionDetails(await api.definitionDetails(item!.id!))
	}

	setDefinitionDetails (definitionDetails: DataDefinitionWithFields) {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		this.inheritedFromLoaded = definitionDetails.inheritedFrom

		if (definitionDetails.inherited) {
			definitionDetails.inherited.forEach((inherited: DataDefinitionWithFields) => {
				inherited.fields.forEach((inheritedField: FieldInstance) => {
					inheritedField.inheritedFrom = inherited.title
					inheritedField._rowVariant = "muted"

					const templateField = definitionDetails.fields.firstOrDefault(templateField =>
						inheritedField.field.id === templateField.field.id)

					if (templateField) {
						templateField.inheritedFrom = inherited.title
						templateField._rowVariant = "muted"
					} else {
						definitionDetails.fields.push(inheritedField)
					}
				})
			})
		}

		definitionDetails.sections = []
		definitionDetails.sectionFields = definitionDetails.fields.reduce((group: any, field: FieldInstance) => {
			var section = field.section ? field.section : "General"

			field.field.dataTypeTitle = this.fieldTypesById[field.field.dataType!].title

			if (field.field.dataSourceId && this.dataSourceDictionary[field.field.dataSourceId]) {
				field.field.dataSourceTitle = this.dataSourceDictionary[field.field.dataSourceId].title
			}

			const fieldDefinition = this.fields.find(f => f.id === field.field.id)
			if (fieldDefinition) {
				Vue.set(fieldDefinition, "added", true)
			}

			if (definitionDetails.sections.indexOf(section) === -1) {
				definitionDetails.sections.push(section)
			}

			(group[section] = group[section] || []).push(field)
			return group
		}, {})

		for (const section in definitionDetails.sectionFields) {
			definitionDetails.sectionFields[section].sortBy("order")
		}

		return this.definitionDetails = definitionDetails
	}

	createNewItem () {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		return this.definitionDetails = {
			title: "",
			description: "",
			inheritedFrom: null,
			inheritedFromTitle: null,
			sections: [],
			sectionFields: {},
			fields: [],
			itemTitleFormat: ""
		}
	}

	async save (template: DataDefinitionWithFields) {
		for (const section in template.sectionFields) {
			template.sectionFields[section].forEach((field: FieldInstance, index: number) => {
				if (!field.inheritedFrom) {
					field.section = section
					field.order = index
				}
			})
		}

		template.inherited = null
		await api.definitionSave(template)
		this.loadDefinitions()
	}

	async deleteItem (template: DataDefinitionWithFields) {
		await api.definitionDelete(template)
		this.loadDefinitions()
	}

	async addFields (addFields: Field[]) {
		if (!this.definitionDetails) {
			return
		}

		if (!this.addFieldToSection) {
			return
		}

		addFields.forEach(field => {
			field.added = true

			if (!this.definitionDetails!.fields.some((caseField: FieldInstance) => caseField.field.id === field.id)) {
				const newCaseField = {
					field,
					section: this.addFieldToSection,
					order: 0,
					requiredRecord: false,
					requiredCase: false,
					permissions: {
						case: null,
						record: null
					},
					value: null,
					fromData: null,
					includeInTable: true
				} as FieldInstance

				this.definitionDetails!.fields.push(newCaseField)
				if (!this.definitionDetails!.sectionFields[this.addFieldToSection!]) {
					Vue.set(this.definitionDetails!.sectionFields, this.addFieldToSection!, [])
				}

				if (this.definitionDetails!.sections.indexOf(this.addFieldToSection!) === -1) {
					this.definitionDetails!.sections.push(this.addFieldToSection!)
				}

				this.definitionDetails!.sectionFields[this.addFieldToSection!].push(newCaseField)
			}
		})

		this.addFieldToSection = null
	}

	async removeField (field: FieldInstance) {
		if (!this.definitionDetails) {
			return
		}

		const fieldDefinition = this.fields.find(f => f.id === field.field.id)
		if (fieldDefinition) {
			fieldDefinition.added = false
		}

		this.definitionDetails!.fields.removeWhere(f => f === field)
		this.definitionDetails!.sectionFields[field.section!].removeWhere((f: FieldInstance) => f === field)
	}

	async addNewField (fieldId: string) {
		await this.loadFields()
		this.setDefinitionDetails(this.definitionDetails!)

		const newField = this.fields.firstOrDefault((f: Field) => f.id === fieldId)
		this.addFields([newField])
	}

	createSection (newSectionName: string) {
		if (newSectionName) {
			if (!this.definitionDetails!.sectionFields[newSectionName!]) {
				Vue.set(this.definitionDetails!.sectionFields, newSectionName!, [])
				this.definitionDetails!.sections.push(newSectionName)
			}
		}
	}

	sectionNameChange (oldValue: string, newValue: string) {
		const changedFields: FieldInstance[] = []

		this.definitionDetails!.sectionFields[oldValue].forEach((f: FieldInstance) => {
			f.section = newValue
			changedFields.push(f)
		})

		if (!this.definitionDetails!.sectionFields[newValue]) {
			Vue.set(this.definitionDetails!.sectionFields, newValue, [])
		}

		this.definitionDetails!.sectionFields[newValue] =
			this.definitionDetails!.sectionFields[newValue].concat(changedFields)

		this.definitionDetails!.sectionFields[oldValue] = this.definitionDetails!.sectionFields[oldValue]
			.filter((f: FieldInstance) => f.section === oldValue)

		if (this.definitionDetails!.sections.indexOf(newValue) === -1) {
			this.definitionDetails!.sections.push(newValue)
		}

		if (this.definitionDetails!.sectionFields[oldValue].length === 0) {
			delete this.definitionDetails!.sectionFields[oldValue]
			this.definitionDetails!.sections.removeWhere(s => s === oldValue)
		}
	}

	public get (item: any, columnKey: string): any {
		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.get(item[topLevel], rest)
		}

		return item[columnKey]
	}

	public canDeleteDefinition (definition: DataDefinitionWithFields): boolean {
		return definition.specialType === null
	}

	public async canConfirmDelete (definition: DataDefinitionWithFields) {
		if (!definition!.id) return false
		var defUsage = await api.definitionUsage(definition)
		return defUsage.dataSources.length === 0
	}

	async deleteDetails (definition: DataDefinition) {
		if (!definition!.id) return
		return api.definitionUsage(definition)
	}

	get sectionDragOptions () {
		return {
			animation: 200,
			group: "sections",
			disabled: false,
			ghostClass: "ghost"
		}
	}
	get fieldDragOptions () {
		return {
			animation: 200,
			group: "fields",
			disabled: false,
			ghostClass: "ghost"
		}
	}

	private isMapField (field: FieldInstance) {
		return field.field.dataType === "map-marker" ||
							field.field.dataType === "map-path" ||
							field.field.dataType === "map-region"
	}
}
